import { useState } from "react";
import { toast } from "sonner";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { shuffle } from "../beatUtils";
import { Briefing } from "./Shared";
import { Tags, Check } from "lucide-react";

export function AssembleBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const [tokens] = useState(() => shuffle(beat.tokens));
  const [assign, setAssign] = useState({});
  const [selTok, setSelTok] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const usedTokenIds = new Set(Object.values(assign));
  const tokenById = (id) => beat.tokens.find((t) => t.id === id);
  const placeInZone = (zoneId) => {
    if (checked || !selTok) return;
    setAssign((a) => {
      const n = { ...a };
      for (const z of Object.keys(n)) if (n[z] === selTok) delete n[z];
      n[zoneId] = selTok;
      return n;
    });
    setSelTok(null);
  };
  const clearZone = (zoneId) => { if (checked) return; setAssign((a) => { const n = { ...a }; delete n[zoneId]; return n; }); };
  const onPrimary = () => {
    if (!checked) {
      if (Object.keys(assign).length < beat.zones.length) return toast.error("Fill every part of the label.");
      const correctCount = beat.zones.filter((z) => assign[z.id] === z.correct).length;
      const full = beat.full ?? 30;
      setRes({ earned: Math.round((full * correctCount) / beat.zones.length), max: full, correctCount,
        detail: `${correctCount}/${beat.zones.length} label fields correct`, key: beat.key, label: beat.label });
    } else onDone(res);
  };
  return (
    <BeatShell Icon={beat.icon || Tags} eyebrow={eyebrow} title={beat.title} subtitle={beat.prompt}
      primaryLabel={checked ? beat.last ? "See debrief →" : "Continue →" : "Check label"} onPrimary={onPrimary} primaryTestId={`${beat.key}-primary-btn`} accent={accent}>
      {showBriefing && <Briefing c={c} />}
      {!checked && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Tap a detail, then tap where it goes on the label</div>
          <div className="flex flex-wrap gap-2" data-testid={`${beat.key}-tokens`}>
            {tokens.map((t) => {
              const used = usedTokenIds.has(t.id);
              const sel = selTok === t.id;
              return (
                <button key={t.id} type="button" disabled={used} onClick={() => setSelTok(sel ? null : t.id)} data-testid={`${beat.key}-token-${t.id}`}
                  className={`rounded-md border-2 px-3 py-1.5 text-sm transition-colors ${used ? "opacity-30 border-slate-200 bg-slate-100 text-slate-400" : sel ? "text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
                  style={sel ? { background: accent, borderColor: accent } : undefined}>{t.text}</button>
              );
            })}
          </div>
        </div>
      )}
      <div className="rounded-md border-2 border-slate-300 bg-white p-3 mt-2" data-testid={`${beat.key}-label`}>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2 flex items-center justify-between">
          <span>Dispensing label</span><span>CertIVSims Veterinary Clinic</span>
        </div>
        <div className="space-y-1.5">
          {beat.zones.map((z) => {
            const tid = assign[z.id];
            const tok = tid ? tokenById(tid) : null;
            const ok = checked && tid === z.correct;
            const bad = checked && tid && tid !== z.correct;
            return (
              <div key={z.id} className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500 w-24 sm:w-28 shrink-0">{z.label}</span>
                <button type="button" disabled={checked} onClick={() => (tok ? clearZone(z.id) : placeInZone(z.id))} data-testid={`${beat.key}-zone-${z.id}`}
                  className={`flex-1 text-left rounded border-2 px-2.5 py-1.5 text-sm min-h-[36px] transition-colors ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : bad ? "border-rose-300 bg-rose-50 text-rose-700" : tok ? "border-slate-300 bg-slate-50 text-slate-800" : "border-dashed border-slate-200 text-slate-400"}`}
                  style={!tok && selTok && !checked ? { borderColor: accent } : undefined}>
                  {tok ? tok.text : "Tap to place…"}
                </button>
                {checked && bad && <span className="text-[10px] text-emerald-700 w-28 sm:w-36 shrink-0 leading-tight">✓ {tokenById(z.correct)?.text}</span>}
                {checked && ok && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>
    </BeatShell>
  );
}
